import React, { useContext, useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../contexts/AuthContext";
import { UiContext } from "../contexts/UiContext";
import MarketingWidgetLoader from "../components/MarketingWidgetLoader";
import Seo from "../components/Seo";
import { accentText, BrandMark } from "../pages/marketing/MarketingParts";

const NAV_PRIMARY = ["/pricing", "/install-guide", "/standard", "/partners", "/info"];

export default function MarketingLayout() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useContext(AuthContext);
  const ui = useContext(UiContext);
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const lang = i18n.language === "en" ? "en" : "he";
  const otherLang = lang === "he" ? "en" : "he";

  const setLang = async (lng) => {
    localStorage.setItem("aw_lang", lng);
    await i18n.changeLanguage(lng);
  };

  // Close the drawer on navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [loc.pathname]);

  const navLabel = {
    "/about": t("m_nav_about"),
    "/standard": t("m_nav_standard"),
    "/info": t("m_nav_info"),
    "/partners": t("m_nav_partners"),
    "/pricing": t("m_nav_pricing"),
    "/install-guide": t("m_nav_install"),
    "/support": t("m_nav_support"),
    "/contact": t("m_nav_contact"),
  };
  const allNav = ["/about", "/pricing", "/install-guide", "/standard", "/partners", "/info", "/support", "/contact"];

  const isActive = (to) => loc.pathname === to;

  const seo = (() => {
    const p = loc.pathname || "/";

    // Keep titles keyword-rich but short.
    if (p === "/") {
      return {
        title: t("m_home_title"),
        description: t("m_home_subtitle"),
      };
    }

    if (p === "/pricing") {
      return {
        title: `${t("appName")} | ${t("m_pricing_title")}`,
        description: t("m_pricing_subtitle"),
      };
    }

    if (p === "/install-guide") {
      return {
        title: `${t("appName")} | ${t("m_install_title")}`,
        description:
          lang === "en"
            ? "Install the accessibility widget in minutes: add your site, approve domains, and embed a single snippet."
            : "התקנה מהירה: מוסיפים אתר, מאשרים דומיינים, ומטמיעים snippet אחד באתר.",
      };
    }

    if (p === "/support") {
      return {
        title: `${t("appName")} | ${t("m_support_title")}`,
        description: t("m_support_subtitle"),
      };
    }

    if (p === "/contact") {
      return {
        title: `${t("appName")} | ${t("m_contact_title")}`,
        description:
          lang === "en"
            ? "Contact Accss for a demo, pricing questions, or technical integration help."
            : "דברו איתנו להדגמה, שאלות תמחור, או עזרה טכנית בהטמעה.",
      };
    }

    if (p === "/about") {
      return {
        title: `${t("appName")} | ${t("m_nav_about")}`,
        description:
          lang === "en"
            ? "Accss is a SaaS platform for managing website accessibility: widget, branding, statements, and compliance reporting."
            : "Accss היא פלטפורמת SaaS לניהול נגישות לאתר: וידג׳ט, מיתוג, הצהרה, ודוחות תאימות.",
      };
    }

    if (p === "/standard") {
      return {
        title: `${t("appName")} | ${t("m_nav_standard")}`,
        description:
          lang === "en"
            ? "Guidance for Israeli Standard 5568 and practical accessibility implementation."
            : "מרכז ידע ליישום נגישות ותקן ישראלי 5568 (WCAG) בצורה פרקטית.",
      };
    }

    if (p === "/info") {
      return {
        title: `${t("appName")} | ${t("m_nav_info")}`,
        description:
          lang === "en"
            ? "Knowledge center: accessibility best practices, implementation tips, and compliance documentation."
            : "מרכז מידע: Best Practices לנגישות, הטמעה, ותיעוד תאימות.",
      };
    }

    if (p === "/partners") {
      return {
        title: `${t("appName")} | ${t("m_nav_partners")}`,
        description:
          lang === "en"
            ? "Partner with Accss: manage multiple sites, white-label branding, and client-ready compliance reports."
            : "מסלול שותפים: ניהול Multi‑Site, White‑Label, ודוחות תאימות מוכנים ללקוחות.",
      };
    }

    // Auth pages should generally not be indexed.
    if (p === "/login" || p === "/register" || p === "/forgot-password" || p === "/set-password") {
      return {
        title: `${t("appName")} | ${p === "/login" ? t("m_nav_login") : p === "/register" ? t("m_nav_register") : "Account"}`,
        description: lang === "en" ? "Account access." : "גישה לחשבון.",
        noIndex: true,
      };
    }

    return {
      title: t("appName"),
      description: t("m_home_subtitle"),
    };
  })();

  const langButton = (
    <Button
      color="inherit"
      onClick={() => setLang(otherLang)}
      startIcon={<LanguageRoundedIcon sx={{ fontSize: "18px !important" }} />}
      aria-label={t("mk_switch_lang_aria")}
      sx={{ color: "text.secondary", fontWeight: 600, minWidth: 0, px: 1.25, "&:hover": { color: "text.primary" } }}
    >
      <span lang={otherLang}>{otherLang === "en" ? "English" : "עברית"}</span>
    </Button>
  );

  const themeButton = ui ? (
    <Tooltip title={ui.isDark ? t("theme_light") : t("theme_dark")}>
      <IconButton onClick={ui.toggleThemeMode} aria-label={ui.isDark ? t("theme_light") : t("theme_dark")} sx={{ color: "text.secondary" }}>
        {ui.isDark ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  ) : null;

  const footerCols = [
    { title: t("m_footer_product", "מוצר"), links: ["/pricing", "/install-guide", "/partners"] },
    { title: t("m_footer_company", "חברה"), links: ["/about", "/contact"], labels: { "/about": t("m_footer_about_us") } },
    { title: t("m_footer_support", "תמיכה"), links: ["/support", "/standard", "/info"] },
  ];

  return (
    <Box className="mkt-root" sx={{ minHeight: "100vh", bgcolor: "background.default", display: "flex", flexDirection: "column" }}>
      <Seo title={seo.title} description={seo.description} path={loc.pathname || "/"} lang={i18n.language || "he"} noIndex={!!seo.noIndex} />
      <MarketingWidgetLoader />

      <a className="mkt-skip" href="#main-content">
        {t("mk_skip")}
      </a>

      <AppBar position="sticky" component="header">
        <Container>
          <Toolbar disableGutters sx={{ minHeight: { xs: 60, md: 68 }, gap: 1 }}>
            <BrandMark sx={{ flex: "none", mr: { lg: 3 } }} />

            {/* Desktop navigation */}
            <Box component="nav" aria-label={t("mk_nav_main_aria")} sx={{ display: { xs: "none", lg: "flex" }, flex: 1 }}>
              <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "flex", gap: 0.25 }}>
                {NAV_PRIMARY.map((to) => (
                  <li key={to}>
                    <Button
                      component={Link}
                      to={to}
                      aria-current={isActive(to) ? "page" : undefined}
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        px: 1.5,
                        color: isActive(to) ? accentText : "text.secondary",
                        bgcolor: isActive(to) ? (th) => th.aw.accentSoft : "transparent",
                        "&:hover": { color: "text.primary", bgcolor: (th) => th.aw.soft },
                      }}
                    >
                      {navLabel[to]}
                    </Button>
                  </li>
                ))}
              </Box>
            </Box>
            <Box sx={{ flex: 1, display: { xs: "block", lg: "none" } }} />

            {/* Desktop actions */}
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ display: { xs: "none", lg: "flex" } }}>
              {langButton}
              {themeButton}
              <Divider orientation="vertical" flexItem sx={{ mx: 0.75, my: 1.5 }} />
              {user ? (
                <>
                  <Button color="inherit" onClick={logout} sx={{ color: "text.secondary" }}>
                    {t("logout")}
                  </Button>
                  <Button variant="contained" component={Link} to="/sites">
                    {t("dashboard")}
                  </Button>
                </>
              ) : (
                <>
                  <Button component={Link} to="/login" color="inherit" sx={{ color: "text.primary" }}>
                    {t("m_nav_login")}
                  </Button>
                  <Button variant="contained" component={Link} to="/register">
                    {t("mk_nav_start")}
                  </Button>
                </>
              )}
            </Stack>

            {/* Mobile: register + menu button */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ display: { xs: "flex", lg: "none" } }}>
              {user ? (
                <Button size="small" variant="contained" component={Link} to="/sites">
                  {t("dashboard")}
                </Button>
              ) : (
                <Button size="small" variant="contained" component={Link} to="/register">
                  {t("mk_nav_start")}
                </Button>
              )}
              <IconButton
                onClick={() => setMobileOpen(true)}
                aria-label={t("mk_menu_open")}
                aria-expanded={mobileOpen}
                aria-controls="mkt-mobile-menu"
                sx={{ border: "1px solid", borderColor: "divider", width: 40, height: 40 }}
              >
                <MenuRoundedIcon />
              </IconButton>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile drawer (focus-trapped, Esc closes) */}
      <Drawer
        anchor={lang === "he" ? "right" : "left"}
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{ id: "mkt-mobile-menu", sx: { width: "min(88vw, 340px)", borderRadius: 0, display: "flex", flexDirection: "column" } }}
        sx={{ display: { lg: "none" } }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, height: 60, borderBottom: "1px solid", borderColor: "divider" }}>
          <BrandMark onClick={() => setMobileOpen(false)} />
          <IconButton onClick={() => setMobileOpen(false)} aria-label={t("mk_menu_close")}>
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
        <Box component="nav" aria-label={t("mk_nav_main_aria")} sx={{ p: 1.5, flex: 1, overflowY: "auto" }}>
          <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: 0.5 }}>
            {allNav.map((to) => (
              <li key={to}>
                <Button
                  component={Link}
                  to={to}
                  fullWidth
                  aria-current={isActive(to) ? "page" : undefined}
                  sx={{
                    justifyContent: "flex-start",
                    minHeight: 46,
                    px: 1.75,
                    fontSize: "1rem",
                    color: isActive(to) ? accentText : "text.primary",
                    bgcolor: isActive(to) ? (th) => th.aw.accentSoft : "transparent",
                  }}
                >
                  {navLabel[to]}
                </Button>
              </li>
            ))}
          </Box>
        </Box>
        <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider", display: "grid", gap: 1.25 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {langButton}
            {themeButton}
          </Stack>
          {user ? (
            <>
              <Button variant="contained" component={Link} to="/sites" fullWidth size="large">
                {t("dashboard")}
              </Button>
              <Button variant="outlined" onClick={() => { logout(); setMobileOpen(false); }} fullWidth>
                {t("logout")}
              </Button>
            </>
          ) : (
            <>
              <Button variant="contained" component={Link} to="/register" fullWidth size="large">
                {t("mk_nav_start")}
              </Button>
              <Button variant="outlined" component={Link} to="/login" fullWidth size="large">
                {t("m_nav_login")}
              </Button>
            </>
          )}
        </Box>
      </Drawer>

      <Box component="main" id="main-content" tabIndex={-1} sx={{ flex: 1 }}>
        <Outlet />
      </Box>

      <Box component="footer" sx={{ bgcolor: "background.paper", borderTop: "1px solid", borderColor: "divider", pt: { xs: 7, md: 9 }, pb: 4 }}>
        <Container>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", md: "1.6fr 1fr 1fr 1fr" },
              gap: { xs: 4, md: 6 },
              mb: { xs: 5, md: 7 },
            }}
          >
            <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
              <BrandMark />
              <Typography sx={{ color: "text.secondary", maxWidth: 320, lineHeight: 1.7, mt: 2, fontSize: "0.925rem" }}>
                {t("m_footer_desc")}
              </Typography>
            </Box>
            {footerCols.map((col) => (
              <Box component="nav" key={col.title} aria-label={col.title}>
                <Typography component="h2" sx={{ fontWeight: 700, fontSize: "0.9rem", mb: 2 }}>
                  {col.title}
                </Typography>
                <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: 1.25 }}>
                  {col.links.map((to) => (
                    <li key={to}>
                      <Box
                        component={Link}
                        to={to}
                        sx={{ color: "text.secondary", textDecoration: "none", fontSize: "0.925rem", "&:hover": { color: accentText, textDecoration: "underline" } }}
                      >
                        {(col.labels && col.labels[to]) || navLabel[to]}
                      </Box>
                    </li>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>

          {/* Honest scope note */}
          <Stack
            direction="row"
            spacing={1.25}
            sx={{ p: 2, mb: 3, borderRadius: (th) => `${th.aw.radius.md}px`, bgcolor: (th) => th.aw.softer, border: "1px solid", borderColor: "divider" }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 18, color: "text.secondary", mt: "2px" }} aria-hidden="true" />
            <Typography sx={{ color: "text.secondary", fontSize: "0.825rem", lineHeight: 1.65 }}>{t("mk_footer_disclaimer")}</Typography>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={2}
            sx={{ pt: 3, borderTop: "1px solid", borderColor: "divider" }}
          >
            <Typography sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
              © {new Date().getFullYear()} ACCSS Technologies Inc. {t("m_footer_rights", "כל הזכויות שמורות")}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              {langButton}
              {themeButton}
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
